/**
 * Onboarding Tour — First-time user walkthrough using react-joyride
 * Covers: Dashboard, Customers, Appointments, Invoices, Settings
 */

import { useState, useEffect, useCallback } from 'react';
import { Joyride, STATUS, type EventData, type Controls, type Step } from 'react-joyride';

const TOUR_STORAGE_KEY = 'ishefa_tour_completed';

const tourSteps: Step[] = [
  {
    target: 'body',
    content: "Welcome to Ishefa! Let's take a quick tour of your admin dashboard.",
    placement: 'center',
    skipBeacon: true,
    title: '👋 Welcome to Ishefa',
  },
  {
    target: '[data-tour="nav-dashboard"]',
    content:
      "Your dashboard gives you a real-time overview of today's appointments, pending invoices, and revenue at a glance.",
    title: '📊 Dashboard',
    placement: 'right',
  },
  {
    target: '[data-tour="nav-appointments"]',
    content:
      'View and manage all appointments. Filter by date, status, and customer. Use bulk actions to update multiple appointments at once.',
    title: '📅 Appointments',
    placement: 'right',
  },
  {
    target: '[data-tour="nav-customers"]',
    content:
      'Your customer database. View profiles, track relationship health scores, manage tags, and see full appointment and invoice history.',
    title: '👥 Customers',
    placement: 'right',
  },
  {
    target: '[data-tour="nav-invoices"]',
    content:
      'Create and manage invoices. Send them via email with tracking, download PDFs, and monitor payment status.',
    title: '📄 Invoices',
    placement: 'right',
  },
  {
    target: '[data-tour="nav-expenses"]',
    content:
      'Track business expenses by category, vendor, and payment method. Generate expense reports for tax purposes.',
    title: '🧾 Expenses',
    placement: 'right',
  },
  {
    target: '[data-tour="search-bar"]',
    content:
      'Use global search to quickly find customers, appointments, invoices, and more across the entire system.',
    title: '🔍 Global Search',
    placement: 'bottom',
  },
  {
    target: 'body',
    content:
      "You're all set! Explore the dashboard and start managing your business. You can restart this tour anytime from Settings.",
    placement: 'center',
    title: "🎉 You're Ready!",
  },
];

export default function OnboardingTour() {
  const [run, setRun] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(TOUR_STORAGE_KEY);
    if (!completed) {
      const timer = setTimeout(() => setRun(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleEvent = useCallback((data: EventData, _controls: Controls) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    }
  }, []);

  if (!run) {
    return null;
  }

  return (
    <Joyride
      steps={tourSteps}
      run={run}
      continuous
      onEvent={handleEvent}
      options={{
        primaryColor: 'hsl(25, 95%, 53%)',
        zIndex: 10000,
        arrowColor: '#fff',
        backgroundColor: '#fff',
        textColor: '#1a1a1a',
        overlayColor: 'rgba(0, 0, 0, 0.5)',
        showProgress: true,
        buttons: ['skip', 'back', 'close', 'primary'],
      }}
      styles={{
        buttonPrimary: {
          backgroundColor: 'hsl(25, 95%, 53%)',
          borderRadius: '8px',
          padding: '8px 16px',
          fontSize: '14px',
          fontWeight: 500,
        },
        buttonBack: {
          color: '#666',
          fontSize: '14px',
        },
        buttonSkip: {
          color: '#999',
          fontSize: '13px',
        },
        tooltip: {
          borderRadius: '12px',
          padding: '20px',
          fontSize: '14px',
        },
        tooltipTitle: {
          fontSize: '16px',
          fontWeight: 600,
          marginBottom: '8px',
        },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip Tour',
      }}
    />
  );
}

// Helper to reset tour (for Settings page)
export function resetOnboardingTour() {
  localStorage.removeItem(TOUR_STORAGE_KEY);
  window.location.reload();
}
